import { computed, inject, ref, shallowRef } from "vue";
import { previewEndDate } from "#ce/apps/SpAdmin/SpSlotInspectorApp/dates";

export const STATUS = Object.freeze({
  IDLE: "idle",
  LOADING: "loading",
  RESULTS: "results",
  EMPTY: "empty",
  ERROR: "error",
});

export const SLOT_INSPECTOR_KEY = Symbol("slot-inspector");

export function useSlotInspectorStore() {
  const store = inject(SLOT_INSPECTOR_KEY);
  if (!store) {
    throw new Error("Slot Inspector store must be provided by the app");
  }
  return store;
}

/**
 * Creates the Slot Inspector's state: the chosen start date and the last preview's outcome.
 *
 * @param {Object} options
 * @param {Object} options.api - A Slot Inspector API from `createSlotInspectorApi`.
 * @param {String} options.recordType - `physician` or `location`.
 * @param {String} options.recordId
 * @param {String} options.defaultStartDate - `Y-m-d` date the form starts on.
 */
export function createSlotInspectorStore({ api, recordType, recordId, defaultStartDate }) {
  const startDate = ref(defaultStartDate);
  const status = ref(STATUS.IDLE);
  const report = shallowRef(null);
  const error = shallowRef(null);

  // Identifies the latest preview request. A response to an earlier one, or to one sent before `back()`, is dropped.
  let currentRequestId = 0;

  const endDate = computed(() => previewEndDate(startDate.value));
  const loading = computed(() => status.value === STATUS.LOADING);
  const showingResults = computed(() => status.value === STATUS.RESULTS);

  function setStartDate(value) {
    startDate.value = value ?? "";
  }

  function showReport(payload) {
    report.value = payload;
    status.value = payload.sections.length > 0 ? STATUS.RESULTS : STATUS.EMPTY;
  }

  function showError(failure) {
    error.value = failure;
    status.value = STATUS.ERROR;
  }

  // Previews the record from the start date and shows the report or the error.
  async function submit() {
    currentRequestId += 1;
    const requestId = currentRequestId;

    status.value = STATUS.LOADING;
    error.value = null;

    let payload;

    try {
      payload = await api.preview({ recordType, recordId, startDate: startDate.value });
    } catch (failure) {
      if (requestId === currentRequestId) {
        showError(failure);
      }
      return;
    }

    if (requestId === currentRequestId) {
      showReport(payload);
    }
  }

  // Returns to the form, keeping the start date and the last report.
  function back() {
    currentRequestId += 1;
    status.value = STATUS.IDLE;
  }

  return {
    startDate,
    endDate,
    status,
    report,
    error,
    loading,
    showingResults,
    setStartDate,
    submit,
    back,
  };
}
